import { useEffect, useRef } from 'react';
import { acceptInit, isAllowedParentOrigin, PARENT_ORIGINS } from './parentOrigin';

type InitMessage = {
  type: 'INIT';
  parentOrigin: string;
  nonce?: string;
};

type ParentMessage = InitMessage;

type ChildMessage = { type: 'READY' } | { type: 'FLOW_SUCCESS'; payload: any; nonce?: string } | { type: 'FLOW_ERROR'; payload: any; nonce?: string };

// The resident portal opens this app with window.open, waits for READY, answers with INIT
// (its origin + a nonce), and takes FLOW_SUCCESS / FLOW_ERROR back. INIT used to be accepted from
// any origin, and results were posted to whatever origin the message named — so any page that
// opened the app could have them sent to itself. Now only allowlisted origins are listened to or
// posted to, and the reply target is the browser-verified event.origin.
export function useParentMessaging() {
  const parentOriginRef = useRef<string | null>(null);
  const nonceRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);

  // Notify parent that child is ready
  useEffect(() => {
    if (!window.opener) return;

    // READY carries nothing, but still only goes to a possible parent: postMessage silently drops
    // it for a non-matching opener. Non-prod builds also accept any localhost port, which can't
    // be enumerated, so they announce to '*'.
    const targets = process.env.REACT_APP_APP_ENV === 'prod' ? PARENT_ORIGINS : ['*'];
    targets.forEach(target => window.opener.postMessage({ type: 'READY' } satisfies ChildMessage, target));
  }, []);

  // Listen for INIT from parent
  useEffect(() => {
    const handler = (event: MessageEvent<ParentMessage>) => {
      // Opener only, allowlisted origin, matching parentOrigin; the decision and its spec live in
      // parentOrigin.ts. The reply origin is event.origin, never what the message claims.
      const replyOrigin = acceptInit(event, window.opener);
      if (!replyOrigin) return;

      // Store parent origin + nonce
      parentOriginRef.current = replyOrigin;
      nonceRef.current = typeof event.data.nonce === 'string' ? event.data.nonce : null;
      isInitializedRef.current = true;
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const sendToParent = (message: ChildMessage) => {
    if (!window.opener) {
      console.warn('Parent window not available');
      return;
    }

    if (!parentOriginRef.current) {
      console.warn('Parent origin not initialized yet');
      return;
    }

    // Belt and braces: never post a result to an origin that isn't on the list.
    if (!isAllowedParentOrigin(parentOriginRef.current)) return;

    window.opener.postMessage(message, parentOriginRef.current);
  };

  const sendSuccess = (payload: any) => {
    if (!isInitializedRef.current) return;

    sendToParent({
      type: 'FLOW_SUCCESS',
      payload,
      nonce: nonceRef.current ?? undefined,
    });
  };

  const sendError = (payload: any) => {
    if (!isInitializedRef.current) return;

    sendToParent({
      type: 'FLOW_ERROR',
      payload,
      nonce: nonceRef.current ?? undefined,
    });
  };

  return {
    sendSuccess,
    sendError,
    isReady: isInitializedRef.current,
  };
}
